/**
 * How a font CHOICE is stored, everywhere in the app.
 *
 * There are two kinds of font and one place they are picked. A Google font is stored by its
 * family name ("Work Sans") - that name goes straight into the css2 URL, so it has to stay on
 * the EPK_FONTS allowlist. An uploaded font is stored as `custom:<id>`, because two people can
 * upload two different files both called "Helvetica" and the family name is not an identity.
 *
 * Everything that reads a font choice - the app's own base font, the press kit, the smart link,
 * the reels - parses it through here, so a stored value can never mean one thing on one surface
 * and something else on another.
 */

import { EPK_FONT_WEIGHTS, type EpkFont } from "./constants.ts";
import { isEmptyString } from "./guards.ts";

export const CUSTOM_FONT_PREFIX = "custom:";

/**
 * The typeface every surface draws when nobody picked one (owner, 2026-08-17).
 *
 * ⚠ **"No choice" is not the same as "no font", and it used to be.** An unset value meant the
 * OS's own UI face - San Francisco on a Mac, Segoe on Windows, Roboto on Android - so the app, the
 * press kit and the smart link each looked like whatever machine happened to be reading them, and
 * the product had no typographic identity at all until an owner went and picked one per surface.
 * A default is the setting almost nobody changes, so the default IS the brand: this is the one
 * value that decides what Get Heard looks like.
 *
 * It is typed as an `EpkFont` on purpose - the default has to stay on the allowlist, or the family
 * name goes into a css2 URL that Google answers with nothing and every surface silently falls back
 * to the system stack this constant exists to replace. Deleting it from `EPK_FONTS` is a
 * typecheck failure rather than a font that quietly stops loading.
 *
 * ⚠ The system stack is still written after it in every `font-family` declaration, and stays: it is
 * what draws while the webfont loads (`display=swap`) and what draws forever for a reader whose
 * network never delivers it.
 */
export const DEFAULT_FONT_FAMILY: EpkFont = "Work Sans";

/**
 * The APP's own reading face (round 306, §AL round 1b), split from `DEFAULT_FONT_FAMILY` above.
 *
 * ⚠ **One constant used to answer two questions** - what the app reads in and what a published page
 * reads in - and the redesign the owner approved on 2026-09-22 moves the first and keeps the second.
 * So they are two constants now, and each reader has to say which surface it means: the Settings font
 * picker's "Default" label and the app's fallback stack read this one; every published-page renderer,
 * `surfaceFont()` and the per-release font pickers still read `DEFAULT_FONT_FAMILY`.
 *
 * Deliberately a plain string and NOT an `EpkFont`: it is self-hosted by `public/style.css` and never
 * built into a Google Fonts URL, so being on the published pages' allowlist would buy nothing.
 */
export const APP_FONT_FAMILY = "Instrument Sans";

/** The font files a font can be uploaded as - see `sniffFontFormat` for why only these two */
export const FONT_FORMATS = ["ttf", "otf"] as const;
export type FontFormat = (typeof FONT_FORMATS)[number];

export interface CustomFont {
	id: number;
	/** The family name read out of the file's own `name` table - not the filename */
	family: string;
	format: FontFormat;
	original_name: string | null;
	size: number;
	/** Tracking in em, applied wherever this font is used (see LETTER_SPACING_*) */
	letter_spacing: number;
	/** CSS weight (100–900), read out of the file's own OS/2 table - a file is ONE weight */
	weight: number;
	/** 0/1, as SQLite stores it */
	italic: number;
	created_at: string;
}

/**
 * Weights, and what type designers call them.
 *
 * A font FILE carries exactly one weight - "Inter" and "Inter Bold" are two files that both call
 * themselves "Inter". So a family is a GROUP of uploads, and a weight picker offers the ones that
 * were actually uploaded. Nothing here synthesizes a bold: a faked bold is a smeared outline, and
 * on a 1080×1920 frame that shows.
 */
export const FONT_WEIGHT_NAMES: Record<number, string> = {
	100: "Thin",
	200: "ExtraLight",
	300: "Light",
	400: "Regular",
	500: "Medium",
	600: "SemiBold",
	700: "Bold",
	800: "ExtraBold",
	900: "Black"
};

/** Reels default to the heaviest weight a family offers up to this - bold reads best on video */
export const DEFAULT_REEL_WEIGHT = 700;

/** "Bold (700)" / "Regular Italic (400)" - how a weight reads in a picker */
export function fontWeightLabel(weight: number, italic?: boolean | number): string {
	const name = FONT_WEIGHT_NAMES[weight] ?? String(weight);
	// `italic` arrives two ways and both are real: a genuine boolean from the font's own OS/2 bits
	// (`utils/fontMetrics.ts`) and the 0/1 INTEGER of `custom_fonts.italic`, because SQLite has no
	// boolean type. Neither reading is the wrong one, so both are spelled out.
	const slanted = italic === true || italic === 1;
	return `${name}${slanted ? " Italic" : ""} (${weight})`;
}

/** A weight value that can be drawn: 100–900 in hundreds, else null */
export function normalizeFontWeight(value: unknown): number | null {
	const weight = typeof value === "number" ? value : Number(value);
	if (!Number.isFinite(weight)) return null;
	const rounded = Math.round(weight / 100) * 100;
	return rounded >= 100 && rounded <= 900 ? rounded : null;
}

/**
 * The weights a Google family can be asked for.
 *
 * Deliberately the same list the press kit already requests (EPK_FONT_WEIGHTS, default
 * 400;600;700): the css2 endpoint rejects the WHOLE request when a family doesn't ship a listed
 * weight, so asking for a weight outside this list is how you get no font at all. These are known
 * good because the EPK loads them.
 */
export function googleFontWeights(family: string): number[] {
	const spec = EPK_FONT_WEIGHTS[family as EpkFont] ?? "400;600;700";
	return spec
		.split(";")
		.map(part => normalizeFontWeight(part.trim()))
		.filter((weight): weight is number => weight !== null)
		.sort((a, b) => a - b);
}

/** The weight a reel should default to in this family: the heaviest up to 700, else the lightest */
export function preferredWeight(weights: number[]): number {
	if (weights.length === 0) return DEFAULT_REEL_WEIGHT;
	const upTo = weights.filter(weight => weight <= DEFAULT_REEL_WEIGHT);
	return upTo.length > 0 ? Math.max(...upTo) : Math.min(...weights);
}

/**
 * How far a font's letter spacing may be pushed, in em.
 *
 * Tracking belongs to the FONT, not to the page: a display face with tight sidebearings reads
 * cramped everywhere it is used, so it is loosened once, on the font, and every surface (the app,
 * the press kit, the smart link, the reels) draws it that way. Tightening is allowed too, but far
 * less of it - negative tracking collides glyphs fast.
 */
export const LETTER_SPACING_MIN = -0.05;
export const LETTER_SPACING_MAX = 0.3;
export const LETTER_SPACING_STEP = 0.005;

/** Clamp a tracking value to something a font can actually be drawn with */
export function clampLetterSpacing(value: number | null | undefined): number {
	if (typeof value !== "number" || !Number.isFinite(value)) return 0;
	return Math.min(LETTER_SPACING_MAX, Math.max(LETTER_SPACING_MIN, value));
}

/** `letter-spacing: 0.03em` - empty when the font is drawn as designed */
export function letterSpacingCss(tracking: number | null | undefined): string {
	const value = clampLetterSpacing(tracking);
	return value === 0 ? "" : `${value}em`;
}

export type FontChoice = { kind: "google"; family: string } | { kind: "custom"; id: number } | null;

/**
 * Read a stored font value. Anything unrecognizable is "no choice".
 *
 * ⚠ This answers what was STORED, and null is a real answer a picker needs - it is the difference
 * between "follow the default" and "somebody chose Work Sans", which look identical on screen and
 * are not the same row. What a surface actually DRAWS for a null is `surfaceFont()`
 * (`services/fonts.ts`), which resolves it to `DEFAULT_FONT_FAMILY`.
 */
export function parseFontValue(value: string | null | undefined): FontChoice {
	if (isEmptyString(value)) return null;
	if (value.startsWith(CUSTOM_FONT_PREFIX)) {
		const id = Number(value.slice(CUSTOM_FONT_PREFIX.length));
		return Number.isInteger(id) && id > 0 ? { kind: "custom", id } : null;
	}
	return { kind: "google", family: value };
}

export function customFontValue(id: number): string {
	return `${CUSTOM_FONT_PREFIX}${id}`;
}

/** The CSS `format()` hint for a face - wrong hints make browsers skip the file entirely */
export function fontCssFormat(format: FontFormat): string {
	return format === "otf" ? "opentype" : "truetype";
}

export function fontMimeType(format: FontFormat): string {
	return format === "otf" ? "font/otf" : "font/ttf";
}

/**
 * A family name, stripped of everything that could break out of a CSS declaration or an HTML
 * attribute. The name comes out of an uploaded file's `name` table, so it is not to be trusted.
 */
export function sanitizeFontFamily(family: string): string {
	return family.replace(/["\\;{}<>]/g, "").trim();
}

/** …and the same name quoted, ready to sit in a `font-family` list */
export function cssFontFamily(family: string): string {
	return `"${sanitizeFontFamily(family)}"`;
}
